// @flow

import { selectPricingInfos } from '@gandi/billing';
import {
  addCartProducts,
  selectOneProductById,
  removeCartProducts,
  selectProductsByType,
  updateCartProduct,
  getCart,
  selectProductsByTypeAndProcess,
} from '@gandi/cart';
import type { ProductId } from '@gandi/cart';
import type { Fqdn } from '@gandi/domain-configuration';
import { fetchMailboxesPrices as fetchMailboxesPricesRRP } from '@gandi/redux-resource-price';
import { selectCurrentId } from '@gandi/redux-sharing';
import type { FetchResponse } from '@gandi/resource/redux';

import { GET } from 'gandi.redux-fetcher';

import { selectSellableMailboxOffers } from '../../modules/mailbox/offers';
import type { MailboxType } from '../../modules/mailbox/offers';
import type { Dispatch, GetState, ThunkAction } from '../../types';

import type { DomainInfo } from '../types';

import { selectMailboxProductById } from './selectors';

/**
 * Fetch mailboxes prices.
 */
export const fetchMailboxesPrices =
  (
    process?: string = 'create',
  ): ((dispatch: Dispatch, getState: GetState) => Promise<any>) =>
  (dispatch: Dispatch, getState: GetState) => {
    const state = getState();
    const { currency, sharingId } = selectPricingInfos(state);

    return dispatch(
      fetchMailboxesPricesRRP({
        process,
        productType: 'mailboxv2',
        currency,
        // sharingId is required when grid is invalid (cf. T107726)
        // So grid is not necessary (and cannot be change in the UI).
        sharingId,
      }),
    );
  };

export const fetchDomainInfos =
  (fqdn: string): ThunkAction<Promise<FetchResponse<200, DomainInfo>>> =>
  dispatch =>
    dispatch(
      GET(`/domain/domains/${fqdn}`, {
        // $FlowFixMe[incompatible-exact]
        on200: (response: FetchResponse<>) => response,
        // $FlowFixMe[incompatible-exact]
        not200: (response: FetchResponse<>) => Promise.reject(response),
      }),
    );

type addMailboxProductsOptions = {
  fqdn?: Fqdn,
  mailbox_type: MailboxType,
  duration: ?number,
  autorenew: boolean,
  ...
};

const mailboxV2ProductType = {
  productType: 'mailboxv2',
};
/**
 * Add mailboxes to cart for "create" process
 */
export const addMailboxProducts =
  (
    { fqdn, mailbox_type, duration, autorenew }: addMailboxProductsOptions,
    count: number,
  ): ThunkAction<Promise<mixed>> =>
  (dispatch: Dispatch, getState: GetState) => {
    const sharingId = selectCurrentId(getState());
    const sellable = selectSellableMailboxOffers(getState(), sharingId);

    const selectedOffer = sellable.find(
      offer => offer.mailbox_type === mailbox_type,
    );

    const { capacity, name, size, unit } = selectedOffer || {};

    const boxProduct = {
      product_type: 'mailboxv2',
      process: 'create',
      payload: {
        capacity,
        name,
        size,
        unit,
        fqdn,
        mailbox_type,
        duration,
        autorenew,
      },
    };

    return dispatch(
      addCartProducts({
        productsData: Array(count).fill(boxProduct),
        extra: {},
        options: {
          headers: { 'X-Dry-Run': 0 },
        },
      }),
    )
      .then(() => dispatch(getCart()))
      .then(() => {
        // remove products in errors
        const mailboxesInError = selectProductsByType(
          getState(),
          mailboxV2ProductType,
        ).filter(
          ({ process, prices, errors = [] }) =>
            process === 'create' && (prices === undefined || errors.length),
        );
        if (mailboxesInError.length) {
          return dispatch(
            removeCartProducts(mailboxesInError.map(({ id }) => ({ id }))),
          );
        }
        return Promise.resolve();
      });
  };

/**
 * Add stand alone mailboxes to cart for "create" process
 */
const addMailboxStandAloneProducts =
  (mailboxProduct: any, count: number): ThunkAction<Promise<mixed>> =>
  (dispatch: Dispatch, getState: GetState) => {
    const { payload } = mailboxProduct;

    const boxProduct = {
      payload: {
        ...payload,
        quantity: count,
      },
    };

    return dispatch(
      updateCartProduct({
        productId: mailboxProduct.id,
        payload: boxProduct.payload,
      }),
    )
      .then(() => dispatch(getCart()))
      .then(() => {
        // remove products in errors
        const mailboxesInError = selectProductsByTypeAndProcess(getState(), {
          process: 'create',
          productType: 'mailbox-standalone',
        }).filter(
          ({ prices, errors = [] }) => prices === undefined || errors.length,
        );
        if (mailboxesInError.length) {
          return dispatch(
            removeCartProducts(mailboxesInError.map(({ id }) => ({ id }))),
          );
        }
        return Promise.resolve();
      });
  };

/**
 * Add or remove `quantity` mailboxes to the cart for the fqnd `fqdn`,
 * according current quantity in the cart.
 */
export const addOrRemoveMailboxProducts =
  ({
    fqdn,
    quantity,
    mailboxType,
    id: mailboxId,
    duration,
  }: {
    fqdn: Fqdn,
    quantity: number,
    mailboxType: MailboxType,
    id: ProductId,
    duration?: number,
  }): ThunkAction<Promise<void>> =>
  (dispatch: Dispatch, getState: GetState) => {
    // compute the quantity so that we add/remove only the diff (Qinput - Qcart)
    const mailboxProduct = selectMailboxProductById(getState(), {
      id: mailboxId,
    });
    const cartQuantity = mailboxProduct?.ids.length || 0;
    const relativeQuantity =
      parseInt(quantity, 10) - parseInt(cartQuantity, 10);

    // If new quantity is more than cart
    if (relativeQuantity > 0) {
      return dispatch(
        addMailboxProducts(
          {
            fqdn,
            mailbox_type: mailboxType,
            // Every mailbox should have the same duration
            duration: duration || mailboxProduct?.payload.duration,
            // Activate autorenew for monthly mailboxes
            autorenew: mailboxProduct?.payload.duration === 1,
          },
          relativeQuantity,
        ),
      );
    }

    if (relativeQuantity < 0) {
      const boxesToRemove =
        mailboxProduct?.ids
          .filter(id => {
            const product = selectOneProductById(getState(), { productId: id });
            return product && !product.isWorking;
          })
          .slice(relativeQuantity) || [];

      return dispatch(removeCartProducts(boxesToRemove.map(id => ({ id }))));
    }
    // If new quantity is equal to current cart, do nothing.
    return Promise.resolve();
  };

/**
 * Add or remove `quantity` stand alone mailboxes to the cart,
 * according current quantity in the cart.
 */
export const addOrRemoveMailboxStandAloneProducts =
  ({
    quantity,
    id,
  }: {
    quantity: number,
    id: ProductId,
  }): ThunkAction<Promise<void>> =>
  (dispatch: Dispatch, getState: GetState) => {
    const mailboxProduct = selectMailboxProductById(getState(), {
      id,
    });

    const cartQuantity = mailboxProduct?.ids.length || 0;
    const relativeQuantity =
      parseInt(quantity, 10) - parseInt(cartQuantity, 10);

    /**
     * If new quantity is more than cart.
     * Either way, we can't have a negative quantity.
     *
     * We can only add or completely remove a product.
     */
    if (relativeQuantity !== 0) {
      return dispatch(addMailboxStandAloneProducts(mailboxProduct, quantity));
    }

    // If new quantity is equal to current cart, do nothing.
    return Promise.resolve();
  };
